/**
 * Journey player capability header.
 *
 * Installed native apps keep old JavaScript for months (no over-the-air
 * updates). A row-level security rule on `journeys` hides published
 * `course_v1` Journeys from any request that does not carry this header, so
 * builds with the old player never see rows they cannot render. The header
 * declares what this build can render; it grants no access by itself.
 *
 * The header goes on PostgREST requests only. Edge Function, Storage and Auth
 * requests must not carry it: the functions' CORS allow-list does not include
 * it and a browser preflight would fail.
 */
export const JP_JOURNEY_PLAYER_HEADER = 'x-jp-journey-player';
export const JP_JOURNEY_PLAYER_CAPABILITY = 'course_v1';

const REST_PATH_PREFIX = '/rest/v1/';

export function isRestRequestUrl(url: string): boolean {
  try {
    return new URL(url).pathname.startsWith(REST_PATH_PREFIX);
  } catch {
    return false;
  }
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

/** Wraps a fetch so REST requests declare the course player capability. */
export function withJourneyPlayerCapability(baseFetch: typeof fetch): typeof fetch {
  return (input, init) => {
    if (!isRestRequestUrl(requestUrl(input))) return baseFetch(input, init);
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set(JP_JOURNEY_PLAYER_HEADER, JP_JOURNEY_PLAYER_CAPABILITY);
    return baseFetch(input, { ...init, headers });
  };
}
